import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { AutoPrompt, ChatMessage } from './ai-agent-tool.data';
import { ChatControllerService } from '../../services/chat-controller/chat-controller.service';
import { ThreedProject } from '../../services/project-controller/project-controller';
import { ProjectControllerService } from '../../services/project-controller/project-controller.service';
import { Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { PartListControllerService } from '../../services/part-list-controller/part-list-controller.service';
import { CanvasControllerService } from '../../../../../../services/canvas-controller/canvas-controller.service';

@Component({
  selector: 'ai-agent-tool',
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './ai-agent-tool.component.html',
  styleUrl: './ai-agent-tool.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiAgentToolComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('messageField') messageField: ElementRef<HTMLDivElement> | undefined;

  public isLoading: boolean = false;

  public project: ThreedProject | undefined;

  public messageBox: ChatMessage[] = [];
  public userInput: FormControl = new FormControl({ value: '', disabled: this.isLoading }, [
    Validators.required,
  ]);
  public autoPrompts: AutoPrompt[] = [];

  private _unsubscribeAll: Subject<any> = new Subject();

  constructor(
    private canvasController: CanvasControllerService,
    private partListController: PartListControllerService,
    private chatController: ChatControllerService,
    private projectController: ProjectControllerService,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.getProject();
  }

  ngAfterViewInit(): void {
    this.getMessages();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
    this._unsubscribeAll.unsubscribe();
  }

  public sendMessage(target: any, autoComplete?: string): void {
    if (this.isLoading) return;
    if (!this.project) return;
    if (this.userInput.errors) return;
    this.isLoading = true;
    this.cd.markForCheck();
    this.chatController
      .sendMessage(
        this.userInput.value,
        this.project.uuid,
        this.canvasController.stateManager.getSettings(),
      )
      .then((aiMessageData: ChatMessage) => {
        if (!aiMessageData) {
          this.isLoading = false;
          this.cd.markForCheck();
          return;
        }
        this.setAddPartModelUuids(aiMessageData.partModelUuids);

        aiMessageData.setting.forEach((setting) => {
          const name =
            setting.material.split('.').length > 1
              ? setting.material.split('.')[1]
              : setting.material.split('.')[0];
          this.canvasController.materialsColorChange([name], setting.style.value);
          // this.canvasController.materialsMatteChange([name], setting.isMatte);
        });

        this.isLoading = false;
        target.value = '';
        if (autoComplete) this.chatController.setAutoPrompts(autoComplete);
        this.cd.markForCheck();
      });
  }

  public prompt(text: string, target: any, autoComplete?: string): void {
    this.userInput.setValue(text);
    this.userInput.updateValueAndValidity();
    this.sendMessage(target, autoComplete);
  }

  public resetChatbot(): void {
    this.chatController.resetChatbot(this.project?.uuid || '').then(() => {
      this.isLoading = false;
      this.cd.markForCheck();
    });
  }

  private getProject(): void {
    this.projectController.project$.pipe(takeUntil(this._unsubscribeAll)).subscribe((project) => {
      this.project = project;
      this.cd.markForCheck();
    });
  }

  private getMessages(): void {
    this.chatController.messageBox$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((messageBox) => {
        this.messageBox = messageBox;
        this.cd.markForCheck();
        this.scrollEnd();
      });

    this.chatController.autoPrompts$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((autoPrompts) => {
        this.autoPrompts = autoPrompts;
      });
  }

  private scrollEnd(): void {
    const timer = setTimeout(() => {
      if (!this.messageField?.nativeElement) return;
      this.messageField.nativeElement.scrollTop = this.messageField.nativeElement.scrollHeight;
      clearTimeout(timer);
    }, 10);
  }

  private setAddPartModelUuids(partModelUuids: string[]): void {
    if (partModelUuids.length === 0) return;
    partModelUuids.forEach((partModelUuid) => {
      const category = this.project?.partCategories?.find((category) =>
        category.partModels.find((partModel) => partModel.uuid === partModelUuid),
      );
      if (!category) return;

      const part = category.partModels.find((partModel) => partModel.uuid === partModelUuid);
      if (!part) return;
      this.isLoading = true;
      const beforePart = this.partListController.categoryMap.get(part.category || part.uuid);
      if (beforePart == part) {
        this.partListController.removePart(part);
        this.canvasController.removeModel(part);
        part.isSelected = false;
        this.isLoading = false;
        this.cd.markForCheck();
        return;
      }

      this.canvasController.addModel(part).then(() => {
        part.isSelected = true;
        if (beforePart) {
          beforePart.isSelected = false;
          this.partListController.removePart(beforePart);
          this.canvasController.removeModel(beforePart);
        }
        this.partListController.addPart(part);
        if (category.cameraInfo.position && category.cameraInfo.target) {
          this.canvasController.lookAt(category.cameraInfo.position, category.cameraInfo.target);
        }
        this.isLoading = false;
        this.cd.markForCheck();
      });
    });
  }
}
